import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from './format.js';

export interface Loaded<T> {
  data: T | undefined;
  loading: boolean;
  error: string;
  /** Re-run the fetch (e.g. after a mutation). Keeps stale data visible meanwhile. */
  reload: () => Promise<void>;
}

/**
 * Load data on mount with loading/error state and a `reload` for after
 * mutations. `fn` must be stable for the life of the component (module-level
 * or wrapped in useCallback) — it is deliberately not a dependency.
 */
export function useLoad<T>(fn: () => Promise<T>): Loaded<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const alive = useRef(true);

  const reload = useCallback(async () => {
    setError('');
    try {
      const result = await fnRef.current();
      if (alive.current) setData(result);
    } catch (e) {
      if (alive.current) setError(errorMessage(e, 'Failed to load.'));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { data, loading, error, reload };
}
